import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { X, Loader2, User, Mail, Lock, Calendar, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import LithuanianKeyboard from './LithuanianKeyboard';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

const AuthModal = ({ isOpen, onClose, onSuccess, defaultTab = 'login' }) => {
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeNameField, setActiveNameField] = useState('firstName');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  
  // Refs for name inputs
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    day: '',
    month: '',
    year: '',
    gender: ''
  });

  // Insert Lithuanian letter into active field
  const insertLithuanianLetter = (letter) => {
    if (activeNameField === 'firstName') {
      setRegisterData(prev => ({
        ...prev,
        firstName: prev.firstName + letter
      }));
      firstNameRef.current?.focus();
    } else if (activeNameField === 'lastName') {
      setRegisterData(prev => ({
        ...prev,
        lastName: prev.lastName + letter
      }));
      lastNameRef.current?.focus();
    }
  };

  if (!isOpen) return null;

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
      toast.error('Įveskite teisingą el. pašto adresą');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      if (response.data.success) {
        setForgotPasswordSent(true);
        toast.success('Slaptažodžio atkūrimo nuoroda išsiųsta!');
      }
    } catch (error) {
      // Don't reveal if email exists or not for security
      setForgotPasswordSent(true);
      toast.success('Jei paskyra egzistuoja, slaptažodžio atkūrimo nuoroda bus išsiųsta.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordSent(false);
    setForgotPasswordEmail('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    console.log('[AUTHMODAL DEBUG] handleLogin called');
    console.log('[AUTHMODAL DEBUG] loginData:', loginData);
    
    if (!loginData.email || !loginData.password) {
      console.log('[AUTHMODAL DEBUG] Validation failed - empty fields');
      toast.error('Užpildykite visus laukus');
      return;
    }
    
    console.log('[AUTHMODAL DEBUG] Calling login function...');
    setLoading(true);
    const result = await login(loginData.email, loginData.password);
    console.log('[AUTHMODAL DEBUG] Login result:', result);
    setLoading(false);
    
    if (result.success) {
      console.log('[AUTHMODAL DEBUG] Login successful!');
      toast.success('Sėkmingai prisijungėte!');
      onSuccess?.();
      onClose();
    } else {
      console.log('[AUTHMODAL DEBUG] Login failed:', result.error);
      toast.error(result.error);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!registerData.email || !registerData.password || !registerData.firstName || 
        !registerData.lastName || !registerData.day || !registerData.month || !registerData.year) {
      toast.error('Užpildykite visus laukus');
      return;
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Slaptažodžiai nesutampa');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('Slaptažodis turi būti bent 6 simbolių');
      return;
    }
    
    const day = parseInt(registerData.day);
    const month = parseInt(registerData.month);
    const year = parseInt(registerData.year);
    
    if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > new Date().getFullYear()) {
      toast.error('Neteisingas gimimo datos formatas');
      return;
    }
    
    setLoading(true);
    const result = await register({
      email: registerData.email,
      password: registerData.password,
      firstName: registerData.firstName,
      lastName: registerData.lastName,
      day,
      month,
      year,
      gender: registerData.gender || null
    });
    setLoading(false);
    
    if (result.success) {
      toast.success('Registracija sėkminga! Sveiki atvykę į NUMENAXIS!');
      onSuccess?.();
      onClose();
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <Card className="bg-gradient-to-br from-[#1a1a3e] to-[#2d1b4e] border-[#d4af37]/30 text-white w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-[#d4af37]">
              {showForgotPassword ? 'Slaptažodžio atkūrimas' : (activeTab === 'login' ? 'Prisijungimas' : 'Registracija')}
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Forgot Password View */}
          {showForgotPassword ? (
            <div className="space-y-4">
              {!forgotPasswordSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-gray-300">
                    Įveskite savo el. pašto adresą ir mes atsiųsime jums slaptažodžio atkūrimo nuorodą.
                  </p>
                  <div>
                    <Label htmlFor="forgot-email" className="text-sm text-gray-300 flex items-center gap-2">
                      <Mail className="w-4 h-4" /> El. paštas
                    </Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white"
                      placeholder="jusu@email.com"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold py-3"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Siunčiama...
                      </>
                    ) : (
                      'Siųsti atkūrimo nuorodą'
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="w-full flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Grįžti į prisijungimą
                  </button>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-green-400" />
                  </div>
                  <p className="text-gray-300">
                    Jei paskyra su šiuo el. paštu egzistuoja, slaptažodžio atkūrimo nuoroda buvo išsiųsta.
                  </p>
                  <p className="text-sm text-gray-500">
                    Patikrinkite savo el. paštą ir spam/šiukšlių aplanką.
                  </p>
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="w-full flex items-center justify-center gap-2 text-sm text-[#d4af37] hover:text-[#f4cf67] transition-colors mt-4"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Grįžti į prisijungimą
                  </button>
                </div>
              )}
            </div>
          ) : (
          <>
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === 'login' ? 'default' : 'outline'}
              className={activeTab === 'login' 
                ? 'flex-1 bg-[#d4af37] hover:bg-[#c4a030] text-black' 
                : 'flex-1 border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10'}
              onClick={() => setActiveTab('login')}
            >
              Prisijungti
            </Button>
            <Button
              variant={activeTab === 'register' ? 'default' : 'outline'}
              className={activeTab === 'register' 
                ? 'flex-1 bg-[#d4af37] hover:bg-[#c4a030] text-black' 
                : 'flex-1 border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37]/10'}
              onClick={() => setActiveTab('register')}
            >
              Registruotis
            </Button>
          </div>

          {/* Login Form */}
          {activeTab === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-sm text-gray-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> El. paštas
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white"
                  placeholder="jusu@email.com"
                />
              </div>
              
              <div>
                <Label htmlFor="login-password" className="text-sm text-gray-300 flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Slaptažodis
                </Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold py-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Jungiamasi...
                  </>
                ) : (
                  'Prisijungti'
                )}
              </Button>
              
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="w-full text-center text-sm text-[#d4af37] hover:text-[#f4cf67] transition-colors mt-2"
              >
                Pamiršau slaptažodį
              </button>
            </form>
          )}

          {/* Register Form */}
          {activeTab === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="reg-firstname" className="text-sm text-gray-300 flex items-center gap-2">
                    <User className="w-4 h-4" /> Vardas
                  </Label>
                  <Input
                    id="reg-firstname"
                    ref={firstNameRef}
                    type="text"
                    value={registerData.firstName}
                    onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
                    onFocus={() => setActiveNameField('firstName')}
                    className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white"
                    placeholder="Jonas"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-lastname" className="text-sm text-gray-300">Pavardė</Label>
                  <Input
                    id="reg-lastname"
                    ref={lastNameRef}
                    type="text"
                    value={registerData.lastName}
                    onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                    onFocus={() => setActiveNameField('lastName')}
                    className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white"
                    placeholder="Jonaitis"
                  />
                </div>
              </div>
              
              {/* Lithuanian Keyboard for name fields */}
              <LithuanianKeyboard onInsert={insertLithuanianLetter} compact={true} />
              
              <div>
                <Label htmlFor="reg-email" className="text-sm text-gray-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> El. paštas
                </Label>
                <Input
                  id="reg-email"
                  type="email"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white"
                  placeholder="jusu@email.com"
                />
              </div>
              
              <div>
                <Label className="text-sm text-gray-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Gimimo data
                </Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={registerData.day}
                    onChange={(e) => setRegisterData({ ...registerData, day: e.target.value.replace(/\D/g, '') })}
                    className="bg-[#0f0f23]/50 border-[#d4af37]/30 text-white text-center"
                    placeholder="DD"
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={2}
                    value={registerData.month}
                    onChange={(e) => setRegisterData({ ...registerData, month: e.target.value.replace(/\D/g, '') })}
                    className="bg-[#0f0f23]/50 border-[#d4af37]/30 text-white text-center"
                    placeholder="MM"
                  />
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={registerData.year}
                    onChange={(e) => setRegisterData({ ...registerData, year: e.target.value.replace(/\D/g, '') })}
                    className="bg-[#0f0f23]/50 border-[#d4af37]/30 text-white text-center"
                    placeholder="YYYY"
                  />
                </div>
              </div>
              
              {/* Gender Selection */}
              <div>
                <Label className="text-sm text-gray-300 mb-2 block">Kaip į tave kreiptis?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setRegisterData({ ...registerData, gender: 'female' })}
                    className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      registerData.gender === 'female'
                        ? 'border-purple-500 bg-purple-500/20 text-purple-400'
                        : 'border-[#d4af37]/30 bg-[#0f0f23]/50 text-gray-400 hover:border-purple-500/50'
                    }`}
                  >
                    <span>♀</span>
                    <span className="text-sm">Moteris</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRegisterData({ ...registerData, gender: 'male' })}
                    className={`p-3 rounded-lg border-2 flex items-center justify-center gap-2 transition-all ${
                      registerData.gender === 'male'
                        ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                        : 'border-[#d4af37]/30 bg-[#0f0f23]/50 text-gray-400 hover:border-blue-500/50'
                    }`}
                  >
                    <span>♂</span>
                    <span className="text-sm">Vyras</span>
                  </button>
                </div>
                <p className="text-[10px] text-gray-500 mt-1 text-center">Naudojama tik pasisveikinimo kalbai</p>
              </div>
              
              <div>
                <Label htmlFor="reg-password" className="text-sm text-gray-300 flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Slaptažodis
                </Label>
                <div className="relative">
                  <Input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white pr-10"
                    placeholder="Min. 6 simboliai"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="reg-confirm" className="text-sm text-gray-300">Pakartokite slaptažodį</Label>
                <Input
                  id="reg-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={registerData.confirmPassword}
                  onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                  className="mt-1 bg-[#0f0f23]/50 border-[#d4af37]/30 text-white"
                  placeholder="••••••••"
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#d4af37] to-[#f4cf67] hover:from-[#c4a030] hover:to-[#e4bf57] text-black font-bold py-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registruojama...
                  </>
                ) : (
                  'Registruotis'
                )}
              </Button>
              
              <p className="text-xs text-gray-400 text-center">
                Registruodamiesi sutinkate su paslaugų sąlygomis ir privatumo politika.
              </p>
            </form>
          )}
          </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AuthModal;
